import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { AuthRequest } from "../../components/course/types";
import { CheckCircle, Edit, Eye, FileDown, XCircle } from "lucide-react";
import { useToast } from "../../components/ui/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

const AuthRequests = () => {
  const { toast } = useToast();
  const [viewingRequest, setViewingRequest] = useState<AuthRequest | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  
  // Sample data - in a real app, this would come from an API
  const [requests, setRequests] = useState<AuthRequest[]>([
    {
      id: "1",
      userName: "John Doe",
      email: "john.doe@example.com",
      requestType: "Password Reset",
      requestedOn: new Date("2023-10-15T10:30:00"),
      status: "pending",
    },
    {
      id: "2",
      userName: "Jane Smith",
      email: "jane.smith@example.com",
      requestType: "Account Access",
      requestedOn: new Date("2023-10-14T14:25:00"),
      status: "pending",
    },
    {
      id: "3",
      userName: "Robert Johnson",
      email: "robert.j@example.com",
      requestType: "New Registration",
      requestedOn: new Date("2023-10-13T09:15:00"),
      status: "approved",
    },
    {
      id: "4",
      userName: "Alice Williams",
      email: "alice.w@example.com",
      requestType: "Password Reset",
      requestedOn: new Date("2023-10-12T16:45:00"),
      status: "rejected",
    },
  ]);

  const handleApprove = (id: string) => {
    setRequests(requests.map(request => {
      if (request.id === id) {
        return { ...request, status: "approved" };
      }
      return request;
    }));

    const targetRequest = requests.find(r => r.id === id);
    
    toast({
      title: "Request Approved",
      description: `${targetRequest?.requestType} request for ${targetRequest?.userName} has been approved.`,
    });
  };

  const handleReject = (id: string) => {
    setRequests(requests.map(request => {
      if (request.id === id) {
        return { ...request, status: "rejected" };
      }
      return request;
    }));

    const targetRequest = requests.find(r => r.id === id);
    
    toast({
      title: "Request Rejected",
      description: `${targetRequest?.requestType} request for ${targetRequest?.userName} has been rejected.`,
    });

    setShowRejectDialog(false);
    setRejectReason("");
  };

  const openRejectDialog = (request: AuthRequest) => {
    setViewingRequest(request);
    setShowRejectDialog(true);
  };

  const handleViewDetails = (request: AuthRequest) => {
    setViewingRequest(request);
  };

  return (
    <div className="container py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text -2xl font-bold">Authentication Requests</h1>
        <Button><FileDown className="mr-2" /> Export Requests</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pending Authentication Requests</CardTitle>
          <CardDescription>
            Review and manage user authentication and access requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Request ID</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Request Type</TableHead>
                <TableHead>Requested On</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell className="font-medium">REQ-{request.id}</TableCell>
                  <TableCell>
                    <div>
                      <div>{request.userName}</div>
                      <div className="text-sm text-gray-500">{request.email}</div>
                    </div>
                  </TableCell>
                  <TableCell>{request.requestType}</TableCell>
                  <TableCell>
                    {request.requestedOn.toLocaleDateString()} at{" "}
                    {request.requestedOn.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </TableCell>
                  <TableCell>
                    {request.status === "pending" && (
                      <Badge variant="outline" className="bg-yellow-100">Pending</Badge>
                    )}
                    {request.status === "approved" && (
                      <Badge variant="default" className="bg-green-500">Approved</Badge>
                    )}
                    {request.status === "rejected" && (
                      <Badge variant="destructive">Rejected</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" onClick={() => handleViewDetails(request)}>
                            <Eye size={16} className="mr-1" /> View
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Request Details</DialogTitle>
                            <DialogDescription>
                              Information about request REQ-{request.id}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Request ID:</span>
                              <span className="col-span-3">REQ-{viewingRequest?.id}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Name:</span>
                              <span className="col-span-3">{viewingRequest?.userName}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Email:</span>
                              <span className="col-span-3">{viewingRequest?.email}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Request Type:</span>
                              <span className="col-span-3">{viewingRequest?.requestType}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Requested On:</span>
                              <span className="col-span-3">
                                {viewingRequest?.requestedOn.toLocaleDateString()} at{" "}
                                {viewingRequest?.requestedOn.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Status:</span>
                              <span className="col-span-3">
                                {viewingRequest?.status === "pending" && (
                                  <Badge variant="outline" className="bg-yellow-100">Pending</Badge>
                                )}
                                {viewingRequest?.status === "approved" && (
                                  <Badge variant="default" className="bg-green-500">Approved</Badge>
                                )}
                                {viewingRequest?.status === "rejected" && (
                                  <Badge variant="destructive">Rejected</Badge>
                                )}
                              </span>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                      
                      {request.status === "pending" && (
                        <>
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => handleApprove(request.id)}
                          >
                            <CheckCircle size={16} className="mr-1" /> Approve
                          </Button>
                          
                          <Dialog open={showRejectDialog && viewingRequest?.id === request.id} onOpenChange={(open) => !open && setShowRejectDialog(false)}>
                            <DialogTrigger asChild>
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => openRejectDialog(request)}
                              >
                                <XCircle size={16} className="mr-1" /> Reject
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                              <DialogHeader>
                                <DialogTitle>Reject Request</DialogTitle>
                                <DialogDescription>
                                  Please provide a reason for rejecting this request.
                                </DialogDescription>
                              </DialogHeader>
                              <div className="grid gap-4 py-4">
                                <div className="grid grid-cols-4 items-center gap-4">
                                  <span className="font-semibold text-right">Request:</span>
                                  <span className="col-span-3">{viewingRequest?.requestType}</span>
                                </div>
                                <div className="grid grid-cols-4 items-center gap-4">
                                  <span className="font-semibold text-right">User:</span>
                                  <span className="col-span-3">{viewingRequest?.userName}</span>
                                </div>
                                <div>
                                  <Label htmlFor="reason">Reason for rejection</Label>
                                  <Textarea
                                    id="reason"
                                    placeholder="Enter the reason for rejection..."
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    className="mt-1"
                                  />
                                </div>
                              </div>
                              <DialogFooter>
                                <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
                                <Button 
                                  variant="destructive" 
                                  onClick={() => handleReject(viewingRequest?.id || "")}
                                  disabled={!rejectReason.trim()}
                                >
                                  Confirm Rejection
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthRequests;